import { useNavigate } from 'react-router-dom';
import { Plus, ShoppingCart, Truck, CheckCircle, Eye, Send, Loader2 } from 'lucide-react';
import { useOrdemCompra } from '@/features/compras/hooks/useOrdemCompra';
import type { StatusOrdemCompra } from '@/features/compras/types';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';

// USA-COM-07: período de emissão, fornecedor e nº da PO (Desktop FrmCadOrdemCompra › Consulta).
// O período usa o PeriodoFiltro padrão das listas (estado local, vai ao servidor como dtInicio/dtFim).
const PADROES = {
  busca: '',
  status: 'todos',
  fornecedorId: 0,
  pagina: 1,
  tamanhoPagina: 20,
};

const statusConfig: Record<StatusOrdemCompra, { label: string; className: string }> = {
  Rascunho:            { label: 'Rascunho',               className: 'bg-gray-500/10 text-gray-600'    },
  Pendente:            { label: 'Pendente',               className: 'bg-yellow-500/10 text-yellow-600' },
  EmAprovacao:         { label: 'Em Aprovação',           className: 'bg-blue-500/10 text-blue-600'    },
  Aprovada:            { label: 'Aprovada',               className: 'bg-green-500/10 text-green-600'  },
  EnviadaFornecedor:   { label: 'Enviada ao Fornecedor',  className: 'bg-cyan-500/10 text-cyan-600'    },
  ConfirmadaFornecedor:{ label: 'Confirmada',             className: 'bg-teal-500/10 text-teal-600'    },
  ParcialmenteRecebida:{ label: 'Rec. Parcial',           className: 'bg-orange-500/10 text-orange-600' },
  TotalmenteRecebida:  { label: 'Recebida',               className: 'bg-purple-500/10 text-purple-600' },
  Cancelada:           { label: 'Cancelada',              className: 'bg-red-500/10 text-red-600'      },
};

const OrdemCompra = () => {
  const navigate = useNavigate();
  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });
  // Período e ordem vão ao servidor (padrão de listas da v118): a lista cresce sem limite com o tempo.
  const voltarPagina1 = () => setFiltros({ pagina: 1 });
  const periodo   = usePeriodo('30dias', voltarPagina1);
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'valor'>('emissao', true, voltarPagina1);

  const { ordens, total, totalPaginas, porStatus, valorTotal: valorFiltrado, isLoading, isFetching,
          enviarOrdem, enviarFornecedor, isEnviando } =
    useOrdemCompra({
      pagina: pag.pagina,
      tamanhoPagina: pag.tamanhoPagina,
      status: filtros.status === 'todos' ? undefined : filtros.status as StatusOrdemCompra,
      filtros: {
        busca:        filtros.busca || undefined,
        ...periodo.intervalo,
        ordenarPor:   ordenacao.ordenarPor,
        decrescente:  ordenacao.decrescente,
        fornecedorId: filtros.fornecedorId || undefined,
      },
    });

  // Contagens do conjunto filtrado (período/busca), calculadas no servidor — não só desta página.
  const contagem    = porStatus ?? {};
  const aprovadas   = contagem.Aprovada ?? 0;
  const recebidas   = contagem.TotalmenteRecebida ?? 0;
  const emAberto    = Object.entries(contagem)
    .filter(([st]) => st !== 'TotalmenteRecebida' && st !== 'Cancelada')
    .reduce((s, [, qtd]) => s + qtd, 0);
  const valorTotal  = valorFiltrado ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Ordens de Compra</h1>
          <p className="text-muted-foreground mt-1">Pedidos de compra — Purchase Orders (PO)</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/ordens/nova')}>
          <Plus className="h-4 w-4" />Nova Ordem
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-blue-500" />Em Aberto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{emAberto}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />Aprovadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{aprovadas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Truck className="h-4 w-4 text-purple-500" />Recebidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{recebidas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">
              {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">No período e filtros selecionados</p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº da PO ou fornecedor..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={() => { limparFiltros(); periodo.setPeriodo('30dias'); }}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-52" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(statusConfig).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-72">
          <Label className="text-xs text-muted-foreground">Fornecedor</Label>
          <FornecedorCombobox
            value={filtros.fornecedorId || null}
            onChange={(id) => setFiltros({ fornecedorId: id ?? 0 })}
            placeholder="Todos os fornecedores"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Emissão</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
          </div>
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>PO #</CabecalhoOrdenavel>
                <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Data</CabecalhoOrdenavel>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Entrega Prevista</TableHead>
                <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor Total</CabecalhoOrdenavel>
                <TableHead>Status</TableHead>
                <TableHead className="w-28">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : ordens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma ordem neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma ordem encontrada'}
                  </TableCell>
                </TableRow>
              ) : (
                ordens.map(o => {
                  const cfg = statusConfig[o.status] ?? { label: String(o.status), className: '' };
                  return (
                    <TableRow key={o.id}>
                      <TableCell className="font-mono font-bold">#{o.numero}</TableCell>
                      <TableCell>{new Date(o.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>{o.nomeFornecedor ?? '(fornecedor não encontrado)'}</TableCell>
                      <TableCell>
                        {o.dtEntregaPrevista
                          ? new Date(o.dtEntregaPrevista).toLocaleDateString('pt-BR')
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {o.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell>
                        <Badge className={cfg.className}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" title="Ver detalhes"
                            onClick={() => navigate(`/compras/ordens/${o.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {o.status === 'Rascunho' && (
                            <Button size="sm" variant="ghost" title="Enviar para aprovação"
                              disabled={isEnviando}
                              onClick={() => enviarOrdem(o.id)}>
                              <Send className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                          {o.status === 'Aprovada' && (
                            <Button size="sm" variant="ghost" title="Enviar ao fornecedor"
                              disabled={isEnviando}
                              onClick={() => enviarFornecedor(o.id)}>
                              <Truck className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
    </div>
  );
};

export default OrdemCompra;
